import type { CrmRecordSummary } from "@/lib/api/crm/masters";

export interface RecordHierarchy<T extends CrmRecordSummary> {
  recordsById: Map<string, T>;
  /** Records keyed by the id of the record they belong to. */
  childrenByOwner: Map<string, T[]>;
  /** Records with no owner (or whose owner isn't in the group). */
  topLevel: T[];
}

export function buildRecordHierarchy<T extends CrmRecordSummary>(records: T[]): RecordHierarchy<T> {
  const recordsById = new Map(records.map((record) => [record.id, record]));
  const childrenByOwner = new Map<string, T[]>();
  const topLevel: T[] = [];
  for (const record of records) {
    if (record.owner_id && recordsById.has(record.owner_id)) {
      childrenByOwner.set(record.owner_id, [...(childrenByOwner.get(record.owner_id) ?? []), record]);
    } else {
      topLevel.push(record);
    }
  }
  return { recordsById, childrenByOwner, topLevel };
}

/** The top of the owner chain a record sits in (the record itself when it has no owner). */
export function rootRecordOf<T extends CrmRecordSummary>(record: T, recordsById: Map<string, T>): T {
  let current = record;
  const visited = new Set<string>([record.id]);
  while (current.owner_id) {
    const owner = recordsById.get(current.owner_id);
    if (!owner || visited.has(owner.id)) break;
    visited.add(owner.id);
    current = owner;
  }
  return current;
}

/** Every record under a record, at any depth. */
export function descendantsOf<T extends CrmRecordSummary>(recordId: string, childrenByOwner: Map<string, T[]>): T[] {
  const descendants: T[] = [];
  const pending = [...(childrenByOwner.get(recordId) ?? [])];
  const visited = new Set<string>([recordId]);
  for (let record = pending.shift(); record; record = pending.shift()) {
    if (visited.has(record.id)) continue;
    visited.add(record.id);
    descendants.push(record);
    pending.push(...(childrenByOwner.get(record.id) ?? []));
  }
  return descendants;
}
